import type { ReactNode } from 'react';
import { useTranslations } from 'next-intl';
import { Card } from './card';
import { Button } from './button';
import { cn } from '@/lib/utils';

export interface EmptyStateAction {
  label: string;
  onClick: () => void;
}

export interface EmptyStateProps {
  title?: string;
  message?: string;
  /** Replaces the default chart icon. Size it h-8 w-8. */
  icon?: ReactNode;
  action?: EmptyStateAction;
  secondaryAction?: EmptyStateAction;
  className?: string;
}

export default function EmptyState({
  title,
  message,
  icon,
  action,
  secondaryAction,
  className,
}: EmptyStateProps) {
  const t = useTranslations('Common');
  const defaultIcon = (
    <svg
      className="text-primary h-8 w-8"
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
      />
    </svg>
  );

  return (
    <Card className={cn(className)}>
      <div className="p-8 text-center">
        <div className="bg-primary/10 border-primary/20 text-accent mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full border">
          {icon ?? defaultIcon}
        </div>
        <h3 className="mb-2 text-lg font-semibold">{title ?? t('emptyStateTitle')}</h3>
        <p className="text-foreground/80 text-sm">{message ?? t('emptyStateDescription')}</p>
        {action || secondaryAction ? (
          <div className="mt-4 flex flex-wrap justify-center gap-2">
            {action ? <Button onClick={action.onClick}>{action.label}</Button> : null}
            {secondaryAction ? (
              <Button variant="ghost" onClick={secondaryAction.onClick}>
                {secondaryAction.label}
              </Button>
            ) : null}
          </div>
        ) : null}
      </div>
    </Card>
  );
}
